import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import { Menu, X, UtensilsCrossed, Mail } from "lucide-react";
import api from "@/lib/api";

const links = (t) => [
  { to: "/", label: t("home") },
  { to: "/articles", label: t("articles") },
  { to: "/guide", label: t("guide") },
  { to: "/pricing", label: t("pricing") },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

export default function PublicLayout() {
  const { t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [noticeEnabled, setNoticeEnabled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    api.get("/public/config").then(({ data }) => { setNotice(data.notice || ""); setNoticeEnabled(!!data.notice_enabled); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {noticeEnabled && notice ? (
        <div className="bg-thali-saffron text-white text-center text-sm py-2 px-4 font-medium" data-testid="site-notice">
          {notice}
        </div>
      ) : null}
      <header className="sticky top-0 z-40 bg-thali-paper/95 backdrop-blur border-b border-thali-turmeric/20">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="w-10 h-10 rounded-xl bg-thali-green text-white grid place-items-center shadow-btn-green">
              <UtensilsCrossed className="w-5 h-5" />
            </span>
            <span className="font-heading font-extrabold text-thali-green leading-tight text-lg">PM POSHAN</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links(t).map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className="px-3 py-2 rounded-lg font-semibold text-thali-ink/80 hover:text-thali-green hover:bg-thali-cream transition-colors">
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            {user && user.role ? (
              <Link to="/home" className="btn-3d-green hidden sm:inline-flex text-sm px-4" data-testid="go-app-btn">{t("dashboard")}</Link>
            ) : (
              <Link to="/login" className="btn-3d-green text-sm px-4" data-testid="header-login-btn">{t("login")}</Link>
            )}
            <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t border-thali-turmeric/20 bg-thali-paper px-4 py-2 flex flex-col">
            {links(t).map((l) => (
              <Link key={l.to} to={l.to} className="py-3 font-semibold text-thali-ink/80" data-testid={`mnav-${l.to === "/" ? "home" : l.to.slice(1)}`}>{l.label}</Link>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-thali-dark text-white/80 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div className="col-span-2 md:col-span-1">
            <div className="font-heading font-extrabold text-white text-lg mb-2">PM POSHAN MDM</div>
            <p className="text-white/60">{t("tagline")}</p>
          </div>
          <div>
            <div className="font-heading font-bold text-white mb-2">Pages</div>
            <ul className="space-y-1">
              <li><Link to="/" className="hover:text-thali-turmeric">{t("home")}</Link></li>
              <li><Link to="/articles" className="hover:text-thali-turmeric">{t("articles")}</Link></li>
              <li><Link to="/guide" className="hover:text-thali-turmeric">{t("guide")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold text-white mb-2">Company</div>
            <ul className="space-y-1">
              <li><Link to="/about" className="hover:text-thali-turmeric">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-thali-turmeric">Contact Us</Link></li>
              <li><Link to="/suggestions" className="hover:text-thali-turmeric">Suggestion Box</Link></li>
              <li><Link to="/#state-request" className="hover:text-thali-turmeric" data-testid="footer-state-request">🗺️ Calculator for your state</Link></li>
              <li><Link to="/calculator-builder" className="hover:text-thali-turmeric" data-testid="footer-builder">🛠️ Build your own calculator</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold text-white mb-2">Legal</div>
            <ul className="space-y-1">
              <li><Link to="/privacy" className="hover:text-thali-turmeric">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-thali-turmeric">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund" className="hover:text-thali-turmeric">Refund Policy</Link></li>
              <li><Link to="/referral-terms" className="hover:text-thali-turmeric">Referral Program Terms</Link></li>
              <li><Link to="/disclaimer" className="hover:text-thali-turmeric">Disclaimer</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-6 text-sm">
          <a href="mailto:TeacherUtilityToolKit@gmail.com" className="inline-flex items-center gap-2 text-white/80 hover:text-thali-turmeric" data-testid="footer-email">
            <Mail className="w-4 h-4" /> TeacherUtilityToolKit@gmail.com
          </a>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/50 px-4">
          © {new Date().getFullYear()} PM POSHAN Calculator. Independent tool, not an official Government website.
        </div>
      </footer>
    </div>
  );
}
